import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { checkRateLimit, recordAttempt, getTimeLeftMessage } from '../../utils/rateLimiter';
import useErrorHandler from '../../hooks/useErrorHandler';
import { ErrorCategory } from '../../utils/errorHandling/errorTypes';
import { ErrorMessage } from '../ErrorHandling';
import { AuthPageShell } from './AuthPageShell';
import './Auth.css';
import './EmailVerification.css';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(auth.currentUser);
  const [isVerified, setIsVerified] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const pollIntervalRef = useRef(null);

  const { handleError, executeWithErrorHandling } = useErrorHandler({
    category: ErrorCategory.AUTHENTICATION,
    onError: (message) => setError(message)
  });

  const checkVerificationStatus = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      await currentUser.reload();
      const updatedUser = auth.currentUser;

      if (updatedUser.emailVerified) {
        setIsVerified(true);
        setIsChecking(false);
        setTimeout(() => {
          navigate('/home');
        }, 2000);
      } else {
        setIsVerified(false);
        setIsChecking(false);
      }
    } catch (err) {
      handleError(err);
      setIsChecking(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const startVerificationFlow = async () => {
      await auth.authStateReady();
      if (cancelled) return;

      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      setUser(currentUser);
      checkVerificationStatus();

      pollIntervalRef.current = setInterval(() => {
        if (!isVerified) {
          checkVerificationStatus();
        }
      }, 5000);
    };

    startVerificationFlow();

    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      setUser(currentUser);
      if (currentUser.emailVerified) {
        setIsVerified(true);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    });

    return () => {
      cancelled = true;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      unsubscribe();
    };
  }, [isVerified, navigate]);

  const handleResendVerification = async () => {
    const rateLimitCheck = checkRateLimit('emailVerificationResends');
    if (!rateLimitCheck.allowed) {
      setError(`Too many resend attempts. Please wait ${getTimeLeftMessage(rateLimitCheck.timeLeft)} before trying again.`);
      setCountdown(rateLimitCheck.timeLeft);
      return;
    }

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      const sendResult = await executeWithErrorHandling(
        async () => {
          await sendEmailVerification(currentUser, {
            url: window.location.origin + '/home',
            handleCodeInApp: false
          });
        },
        [],
        {
          category: ErrorCategory.AUTHENTICATION,
          context: { operation: 'resendEmailVerification' }
        }
      );

      if (sendResult !== null) {
        recordAttempt('emailVerificationResends');
        setCountdown(null);
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return null;
        }
        return prev - 1;
      });
    }, 60000);

    return () => clearInterval(timer);
  }, [countdown]);

  if (isChecking) {
    return (
      <AuthPageShell>
        <div className="flashcode-email-verification-page__status" aria-busy="true">
          <h2 className="flashcode-auth-page__form-title">Checking your email.</h2>
          <p className="flashcode-auth-page__form-subtitle">Hang tight while we confirm your verification status.</p>
          <div
            className="flashcode-email-verification-page__spinner flashcode-auth-page__btn-primary flashcode-auth-page__btn-primary--loading"
            aria-hidden
          />
        </div>
      </AuthPageShell>
    );
  }

  if (isVerified) {
    return (
      <AuthPageShell>
        <div className="flashcode-email-verification-page__status">
          <FaCheckCircle className="flashcode-email-verification-page__success-icon" aria-hidden />
          <h2 className="flashcode-auth-page__form-title">Email verified!</h2>
          <p className="flashcode-auth-page__form-subtitle">
            Your email has been confirmed. Redirecting you now...
          </p>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="flashcode-email-verification-page__icon" aria-hidden>
        <FaEnvelope />
      </div>

      <h2 className="flashcode-auth-page__form-title">Verify your email.</h2>
      <p className="flashcode-auth-page__form-subtitle">One quick step before you continue.</p>

      <p className="flashcode-email-verification-page__label">Email</p>
      <div className="flashcode-email-verification-page__email-display">{user?.email}</div>

      <p className="flashcode-email-verification-page__instructions">
        After sign-up we ask Firebase to send a verification link. If nothing arrives within a minute,
        use <strong>Resend</strong> below and check spam or promotions. If resend shows an error, check
        your browser console (F12) or Firebase Authentication logs.
      </p>

      <div className="flashcode-auth-page__messages">
        {error && <ErrorMessage message={error} type="error" />}
        {resendSuccess && (
          <div className="flashcode-email-verification-page__success-banner" role="status">
            Verification email sent. Please check your inbox.
          </div>
        )}
      </div>

      <div className="flashcode-email-verification-page__actions">
        <button
          type="button"
          onClick={handleResendVerification}
          className={`flashcode-auth-page__btn-primary${isResending ? ' flashcode-auth-page__btn-primary--loading' : ''}`}
          disabled={isResending || countdown !== null}
        >
          {!isResending &&
            (countdown !== null
              ? `Resend in ${countdown} min`
              : 'Resend verification email →')}
        </button>

        <button
          type="button"
          onClick={checkVerificationStatus}
          className={`flashcode-auth-page__btn-primary flashcode-auth-page__btn-primary--secondary${isChecking ? ' flashcode-auth-page__btn-primary--loading' : ''}`}
          disabled={isChecking}
        >
          {!isChecking && "I've verified my email →"}
        </button>
      </div>

      <p className="flashcode-auth-page__guest-row flashcode-email-verification-page__note">
        This page checks for verification every few seconds. Once confirmed, you&apos;ll be redirected
        automatically.
      </p>
    </AuthPageShell>
  );
};

export default EmailVerification;
